import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { Plus, Search, Edit, Trash2, Phone, Mail, MapPin, Eye, Users, Hash, CreditCard } from 'lucide-react';
import { Owner, Pet } from '../types';
import OwnerForm from '../components/Forms/OwnerForm';
import PetForm from '../components/Forms/PetForm';
import DeleteConfirmModal from '../components/Modals/DeleteConfirmModal';
import EmptyState from '../components/EmptyState/EmptyState';
import OwnerAvatar from '../components/Avatar/OwnerAvatar';
import PetAvatar from '../components/Avatar/PetAvatar';
import toast from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getOwners, addOwner, updateOwner, deleteOwner } from '../services/ownerService';
import { getPets, addPet, updatePet } from '../services/petService';
import { supabase } from '../services/mockData';
import { normalizeTr } from '../utils/normalizeTr';

const Owners: React.FC = () => {
  const queryClient = useQueryClient();

  // States for UI management
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOwner, setSelectedOwner] = useState<Owner | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingOwner, setEditingOwner] = useState<Owner | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingOwner, setDeletingOwner] = useState<Owner | null>(null);
  const [showPetForm, setShowPetForm] = useState(false);

  // Fetching owners with useQuery
  const { data: owners = [], isLoading: isOwnersLoading, error: ownersError } = useQuery<Owner[], Error>({ 
    queryKey: ['owners'], 
    queryFn: getOwners 
  });

  // Fetching pets with useQuery
  const { data: pets = [], isLoading: isPetsLoading, error: petsError } = useQuery<Pet[], Error>({ 
    queryKey: ['pets'], 
    queryFn: getPets 
  });

  // Mutations for Create, Update, Delete
  const ownerMutation = useMutation({
    mutationFn: async ({ ownerData, petId, isEditing }: { ownerData: Omit<Owner, 'id' | 'createdAt'>, petId?: string, isEditing: boolean }) => {
      if (isEditing && editingOwner) {
        return updateOwner(editingOwner.id, ownerData);
      } else {
        const newOwner = await addOwner(ownerData);
        if (petId && newOwner.id) {
          await updatePet(petId, { owner_id: newOwner.id });
        }
        return newOwner;
      }
    },
    onSuccess: (_, { petId, isEditing }) => {
      queryClient.invalidateQueries({ queryKey: ['owners'] });
      if (petId) {
        queryClient.invalidateQueries({ queryKey: ['pets'] });
        toast.success('Sahip başarıyla kaydedildi ve hayvan atandı!');
      } else {
        toast.success(isEditing ? 'Sahip başarıyla güncellendi!' : 'Sahip başarıyla eklendi!');
      }
      setShowForm(false);
      setEditingOwner(null);
    },
    onError: (err: any) => toast.error('Hata: ' + err.message)
  });

  const deleteMutation = useMutation({
    mutationFn: deleteOwner,
    onSuccess: () => {
      ['owners', 'pets', 'vaccinations', 'appointments', 'examinations'].forEach(k =>
        queryClient.invalidateQueries({ queryKey: [k] })
      );
      toast.success('Sahip başarıyla silindi!');
      setShowDeleteModal(false);
      setDeletingOwner(null);
    },
    onError: (err: any) => toast.error('Hata: ' + err.message)
  });

  // Event Handlers
  const handleViewOwner = (owner: Owner) => {
    setSelectedOwner(owner);
    setShowModal(true);
  };

  const handleAddOwner = () => {
    setEditingOwner(null);
    setShowForm(true);
  };

  const handleEditOwner = (owner: Owner) => {
    setEditingOwner(owner);
    setShowForm(true);
  };

  const handleDeleteOwner = (owner: Owner) => {
    setDeletingOwner(owner);
    setShowDeleteModal(true);
  };

  const handleSubmitOwner = (ownerData: Omit<Owner, 'id' | 'createdAt'>, petId?: string) => {
    if (editingOwner) {
      const oldEmail = (editingOwner.email || '').trim().toLowerCase();
      const newEmail = (ownerData.email || '').trim().toLowerCase();
      if (oldEmail !== newEmail && !window.confirm('E-posta değişirse hasta sahibi mobil uygulamadan çıkarılır ve yeni e-postayla yeniden giriş yapmalıdır.')) {
        return;
      }
    }
    ownerMutation.mutate({ ownerData, petId, isEditing: !!editingOwner });
  };

  const handleConfirmDelete = () => {
    if (deletingOwner) {
      deleteMutation.mutate(deletingOwner.id);
    }
  };

  const petMutation = useMutation({
    mutationFn: (petData: Omit<Pet, 'id' | 'createdAt' | 'owner'>) => {
      if (!selectedOwner) throw new Error('Sahip seçilmedi!');
      return addPet({ ...petData, owner_id: selectedOwner.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pets'] });
      toast.success('Hasta başarıyla eklendi!');
      setShowPetForm(false);
    },
    onError: (err: any) => toast.error('Hata: ' + err.message)
  });

  const handleAddPet = () => setShowPetForm(true);

  const handleSubmitPet = (petData: Omit<Pet, 'id' | 'createdAt' | 'owner'>) => {
    petMutation.mutate(petData);
  };

  // Filtering and Data Derivation
  const filteredOwners = useMemo(() => owners.filter(owner =>
    normalizeTr(owner.name).includes(normalizeTr(searchTerm)) ||
    owner.phone.includes(searchTerm) ||
    normalizeTr(owner.email).includes(normalizeTr(searchTerm))
  ), [owners, searchTerm]);

  const getOwnerPets = (ownerId: string) => pets.filter(pet => pet.owner_id === ownerId);

  const formatAgeFromMonths = (months: number | undefined) => {
    if (!months && months !== 0) return '';
    if (months < 12) return `${months} ay`;
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    return remainingMonths === 0 ? `${years} yaş` : `${years} yaş ${remainingMonths} ay`;
  };

  // Render Logic
  if (isOwnersLoading || isPetsLoading) return <div className="flex justify-center items-center h-64"><div className="loader"></div></div>;
  if (ownersError || petsError) return <div className="text-center py-12 text-red-500">Hata: {ownersError?.message || petsError?.message}</div>;

  return (
    <div className="page-stack">
      {/* Page Header */}
      <header>
        <div className="page-head">
          <div>
            <h1 className="page-title">Hasta Sahipleri</h1>
            <p className="page-subtitle">Mevcut hasta sahiplerini yönetin veya yeni sahip ekleyin.</p>
          </div>
          <button
            onClick={handleAddOwner}
            className="btn btn-primary self-start sm:self-auto"
          >
            <Plus className="w-4 h-4" />
            <span>Yeni Sahip Ekle</span>
          </button>
        </div>
        <div className="mt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#B0A99A]" />
            <input
              type="text"
              placeholder="İsim, telefon veya e-postaya göre ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10"
            />
          </div>
        </div>
      </header>

      {/* Loading and Empty States */}
      {isOwnersLoading || isPetsLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="loader"></div>
        </div>
      ) : filteredOwners.length === 0 ? (
        <EmptyState
          icon={Users}
          iconColor="teal"
          title="Henüz hayvan sahibi yok"
          description="Müşterilerinizi kaydederek hayvanlarının takibini başlatın. Eklemek için butona tıklayın."
          actionLabel="Yeni Hayvan Sahibi Ekle"
          onAction={() => setShowForm(true)}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredOwners.map((owner, index) => {
            const ownerPets = getOwnerPets(owner.id);
            return (
              <motion.div
                key={owner.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                onClick={() => handleViewOwner(owner)}
                className="dribbble-panel p-4 cursor-pointer group relative"
              >
                {/* Üst — Dinamik renkli avatar + isim */}
                <div className="flex items-start gap-3 mb-3">
                  <OwnerAvatar name={owner.name} size={48} />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#2B2B2B] truncate">{owner.name}</p>
                    <p className="text-xs text-[#8A8676]">
                      {ownerPets.length} hasta
                    </p>
                  </div>
                </div>

                {/* Bilgi rozetleri */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {owner.phone && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#F0EBE1] text-[#2B2B2B] truncate max-w-full">
                      <Phone className="w-3 h-3 shrink-0" />
                      <span className="truncate">{owner.phone}</span>
                    </span>
                  )}
                  {owner.email && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#F0EBE1] text-[#2B2B2B] truncate max-w-full">
                      <Mail className="w-3 h-3 shrink-0" />
                      <span className="truncate">{owner.email}</span>
                    </span>
                  )}
                </div>

                {/* Hastaları — küçük avatarlarla */}
                {ownerPets.length > 0 && (
                  <div className="flex items-center gap-1.5 pt-3 border-t border-[#F0EBE1]">
                    <div className="flex -space-x-1.5">
                      {ownerPets.slice(0, 4).map(pet => (
                        <div key={pet.id} className="ring-2 ring-white rounded-lg" title={pet.name}>
                          <PetAvatar name={pet.name} species={pet.species} size={24} />
                        </div>
                      ))}
                    </div>
                    <span className="text-[11px] text-[#8A8676] font-medium ml-1">
                      {ownerPets.length > 4 ? `+${ownerPets.length - 4} hayvan` :
                        ownerPets.map(p => p.name).join(', ')}
                    </span>
                  </div>
                )}

                {/* Aksiyon — hover ile sağ üstte */}
                <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white/95 rounded-lg p-1 shadow-sm border border-[#F0EBE1]">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleEditOwner(owner); }}
                    className="w-6 h-6 rounded-md hover:bg-[#E8EFD9] text-[#557236] flex items-center justify-center"
                    title="Düzenle"
                  >
                    <Edit className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteOwner(owner); }}
                    className="w-6 h-6 rounded-md hover:bg-red-50 text-red-600 flex items-center justify-center"
                    title="Sil"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Owner Detail Modal */}
      {showModal && selectedOwner && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[9999] flex justify-center items-center p-4" style={{backgroundColor:"rgba(0,0,0,0.75)"}}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="dribbble-panel p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-[#2B2B2B]">Sahip Detayları</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-[#B0A99A] hover:text-[#2B2B2B]"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-[#2B2B2B]">{selectedOwner.name}</h3>
                <p className="text-sm text-[#8A8676]">Kayıt: {new Date(selectedOwner.createdAt).toLocaleDateString()}</p>
              </div>

              <div className="space-y-3 text-[#8A8676] mt-4">
                <div className="flex items-center space-x-3">
                  <Phone className="w-4 h-4 text-[#B0A99A]" />
                  <span className="text-sm">{selectedOwner.phone}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-4 h-4 text-[#B0A99A]" />
                  <span className="text-sm">{selectedOwner.email}</span>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="w-4 h-4 text-[#B0A99A] mt-0.5" />
                  <span className="text-sm">{selectedOwner.address}</span>
                </div>
                {selectedOwner.chipNumber && (
                  <div className="flex items-center space-x-3">
                    <Hash className="w-4 h-4 text-[#B0A99A]" />
                    <span className="text-sm">Çip: {selectedOwner.chipNumber}</span>
                  </div>
                )}
                {selectedOwner.passportNumber && (
                  <div className="flex items-center space-x-3">
                    <CreditCard className="w-4 h-4 text-[#B0A99A]" />
                    <span className="text-sm">Pasaport: {selectedOwner.passportNumber}</span>
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-[#E8E2D4]">
                <h4 className="font-medium text-[#2B2B2B] mb-2">Hastalar</h4>
                {getOwnerPets(selectedOwner.id).length > 0 ? (
                  <div className="space-y-2">
                    {getOwnerPets(selectedOwner.id).map(pet => (
                      <div key={pet.id} className="p-3 bg-[#F7F4ED] rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-[#2B2B2B]">{pet.name}</p>
                            <p className="text-sm text-[#8A8676]">{pet.breed} • {formatAgeFromMonths(pet.age as any)}</p>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            pet.species === 'dog' ? 'bg-blue-100 text-blue-800' :
                            pet.species === 'cat' ? 'bg-orange-100 text-orange-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {pet.species === 'dog' ? 'Köpek' :
                             pet.species === 'cat' ? 'Kedi' :
                             pet.species === 'bird' ? 'Kuş' : 'Diğer'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[#8A8676]">Henüz hasta kaydı bulunmuyor</p>
                )}
              </div>

              <div className="flex space-x-3 pt-4">
                <button 
                  onClick={() => {
                    setShowModal(false);
                    handleEditOwner(selectedOwner);
                  }}
                  className="btn btn-secondary flex-1"
                >
                  Düzenle
                </button>
                <button 
                  onClick={handleAddPet}
                  className="btn btn-primary flex-1"
                >
                  Yeni Hasta Ekle
                </button>
              </div>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Owner Form Modal */}
      <OwnerForm
        owner={editingOwner}
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        onSubmit={handleSubmitOwner}
      />

      {/* Pet Form Modal */}
      {selectedOwner && (
        <PetForm
          pet={{ owner_id: selectedOwner.id } as any} // Pre-fill owner
          isOpen={showPetForm}
          onClose={() => setShowPetForm(false)}
          onSubmit={handleSubmitPet}
          owners={owners} // Pass the owners list
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Sahip Sil"
        message={(() => {
          const n = deletingOwner ? pets.filter(p => p.owner_id === deletingOwner.id).length : 0;
          return n > 0
            ? `Bu sahibe bağlı ${n} hayvan kaydı var. Sahip silindiğinde bu hayvanlar ve onlara ait kayıtlar da silinebilir veya sahipsiz kalabilir. Bu işlem geri alınamaz.`
            : 'Bu sahibi silmek istediğinizden emin misiniz?';
        })()}
        itemName={deletingOwner?.name}
      />
    </div>
  );
};

export default Owners; 